import React from 'react';
import './LayoutVisualization.css';

// Layout type names in Romanian
const layoutNamesRo = {
  'linear': 'linie',
  'L-shape': 'L',
  'U-shape': 'U',
  'back-to-back': 'spate în spate'
};

// Function to calculate space requirements
const calculateSpaceRequirement = (layout, numFractions) => {
  // For 5 fractions, use the exact specified values
  if (numFractions === 5) {
    const fiveContainerSpaces = {
      'linear': 21,
      'L-shape': 20,
      'U-shape': 21,
      'back-to-back': 24
    };
    return fiveContainerSpaces[layout] || 21; // Default to 21 if layout not found
  }
  
  // For other number of fractions, use a base calculation with specific multipliers
  const baseSpacePerContainer = 4; // Base space per container
  
  // Relative multipliers derived from the 5-fraction examples
  const layoutMultipliers = {
    'linear': 21/20, // normalized against L-shape as baseline
    'L-shape': 1,    // baseline
    'U-shape': 21/20,
    'back-to-back': 24/20
  };
  
  const multiplier = layoutMultipliers[layout] || 1;
  
  // Calculate total space (proportional to the specific 5-container case)
  return Math.ceil((numFractions/5) * 20 * multiplier);
};

// Layout descriptions
const renderLayoutInfoTable = (layout, numFractions) => {
    const spaceRequired = calculateSpaceRequirement(layout, numFractions);
    
    const baseDescriptions = {
      'linear': 'Containerele sunt așezate în linie dreaptă, unul lângă altul. Această așezare este ideală pentru spații înguste și lungi.',
      'L-shape': 'Containerele sunt aranjate în formă de L, oferind o utilizare eficientă a colțurilor și a pereților adiacenți.',
      'U-shape': 'Containerele sunt dispuse în formă de U, creând un spațiu central pentru acces ușor la toate containerele.',
      'back-to-back': 'Containerele sunt așezate spate în spate, creând o așezare compactă.'
    };
    
    const description = baseDescriptions[layout] || baseDescriptions['linear'];
    const layoutTypeDisplay = layout === 'back-to-back' ? 
      layoutNamesRo[layout] : 
      `formă de ${layoutNamesRo[layout]}`;
    
    return (
      <table className="layout-info-table">
        <tbody>
          <tr>
            <td>Descriere:</td>
            <td>{description}</td>
          </tr>
          <tr>
            <td>Tip așezare:</td>
            <td>{layoutTypeDisplay}</td>
          </tr>
          <tr>
            <td>Număr de fracții:</td>
            <td>{numFractions}</td>
          </tr>
          <tr>
            <td>Suprafață necesară:</td>
            <td>{spaceRequired} mp</td>
          </tr>
        </tbody>
      </table>
    );
  };

const LayoutVisualization = ({ layout, selectedFractions, fractionNames }) => {
  if (!selectedFractions || selectedFractions.length === 0) {
    return (
      <div className="layout-visualization">
        <p className="preview-text">
          Nu ați selectat nicio fracție. Vă rugăm să selectați cel puțin o fracție.
        </p>
      </div>
    );
  }

  // Layout titles
  const layoutTitle = {
    'linear': 'Așezare Liniară',
    'L-shape': 'Așezare în formă de L',
    'U-shape': 'Așezare în formă de U',
    'back-to-back': 'Așezare Spate în Spate',
    'default': 'Așezare Implicită'
  };

  // Render a container for a fraction
  const renderContainer = (fraction, index) => (
    <div 
      key={`${fraction.name}-${index}`}
      className={`container-box container-${fraction.name} ${fraction.drumType === 'double' ? 'double-drum' : 'single-drum'}`}
    >
      {fractionNames[fraction.name].substring(0, 1)}
    </div>
  );

  // For shapes, we only support up to 5 containers.
  const isShape = layout === 'L-shape' || layout === 'U-shape';
  const effectiveFractions = isShape && selectedFractions.length > 5 
    ? selectedFractions.slice(0, 5)
    : selectedFractions;

  // Default layout if nothing matches
  if (!layout || !layoutTitle[layout]) {
    return (
      <div className="layout-visualization">
        <h3 className="layout-title">{layoutTitle['default']}</h3>
        <div className="layout-description">
          {renderLayoutInfoTable('default', selectedFractions.length)}
        </div>
        <div className="layout-container">
          <div className="container-row">
            {selectedFractions.map(renderContainer)}
          </div>
        </div>
      </div>
    );
  }

  switch (layout) {
    case 'linear':
      return (
        <div className="layout-visualization">
          <h3 className="layout-title">{layoutTitle[layout]}</h3>
          <div className="layout-description">
            {renderLayoutInfoTable(layout, selectedFractions.length)}
          </div>
          <div className="layout-container linear-container">
            <div className="container-row">
              {selectedFractions.map(renderContainer)}
            </div>
          </div>
        </div>
      );

    case 'L-shape': {
      const n = effectiveFractions.length;
      if (n < 3) {
        return (
          <div className="layout-visualization">
            <h3 className="layout-title">{layoutTitle[layout]}</h3>
            <div className="layout-description">
              {renderLayoutInfoTable(layout, effectiveFractions.length)}
            </div>
            <div className="layout-container">
              <div className="container-row">
                {effectiveFractions.map(renderContainer)}
              </div>
            </div>
          </div>
        );
      }
      
      const verticalCount = Math.ceil(n / 2);
      const verticalFractions = effectiveFractions.slice(0, verticalCount);
      const horizontalFractions = effectiveFractions.slice(verticalCount - 1);
      
      return (
        <div className="layout-visualization">
          <h3 className="layout-title">{layoutTitle[layout]}</h3>
          <div className="layout-description">
            {renderLayoutInfoTable(layout, effectiveFractions.length)}
          </div>
          <div className="l-shape-container">
            <div className="l-shape-layout">
              <div className="vertical-column">
                {verticalFractions.map((fraction, index) => renderContainer(fraction, `v-${index}`))}
              </div>
              <div className="horizontal-row">
                {horizontalFractions.length > 1 && horizontalFractions.slice(1).map((fraction, index) => 
                  renderContainer(fraction, `h-${index}`)
                )}
              </div>
            </div>
          </div>
        </div>
      );
    }

    case 'U-shape': {
      const n = effectiveFractions.length;
      if (n < 4) {
        return (
          <div className="layout-visualization">
            <h3 className="layout-title">{layoutTitle[layout]}</h3>
            <div className="layout-description">
              {renderLayoutInfoTable(layout, effectiveFractions.length)}
            </div>
            <div className="layout-container">
              <div className="container-row">
                {effectiveFractions.map(renderContainer)}
              </div>
            </div>
          </div>
        );
      }
      
      let leftColumn = [];
      let bottomRow = [];
      let rightColumn = [];
      
      if (n === 4) {
        leftColumn = [effectiveFractions[0]];
        bottomRow = [effectiveFractions[1], effectiveFractions[2]];
        rightColumn = [effectiveFractions[3]];
      } else if (n >= 5) {
        leftColumn = effectiveFractions.slice(0, 2);
        bottomRow = [effectiveFractions[2], effectiveFractions[3]];
        rightColumn = effectiveFractions.slice(4);
      }
      
      return (
        <div className="layout-visualization">
          <h3 className="layout-title">{layoutTitle[layout]}</h3>
          <div className="layout-description">
            {renderLayoutInfoTable(layout, effectiveFractions.length)}
          </div>
          <div className="u-shape-container">
            <div className="u-shape-layout">
              <div className="side-columns">
                <div className="left-column">
                  {leftColumn.map((fraction, index) => renderContainer(fraction, `l-${index}`))}
                </div>
                <div className="right-column" style={{ justifyContent: 'flex-end' }}>
                  {rightColumn.map((fraction, index) => renderContainer(fraction, `r-${index}`))}
                </div>
              </div>
              <div className="bottom-row">
                {bottomRow.map((fraction, index) => renderContainer(fraction, `b-${index}`))}
              </div>
            </div>
          </div>
        </div>
      );
    }

    case 'back-to-back': {
      const n = selectedFractions.length;
      const firstHalf = Math.ceil(n / 2);
      const topRow = selectedFractions.slice(0, firstHalf);
      const bottomRow = selectedFractions.slice(firstHalf);
      
      return (
        <div className="layout-visualization">
          <h3 className="layout-title">{layoutTitle[layout]}</h3>
          <div className="layout-description">
            {renderLayoutInfoTable(layout, selectedFractions.length)}
          </div>
          <div className="back-to-back-container">
            <div className="back-to-back-aligned-wrapper">
              <div className="top-row">
                {topRow.map((fraction, index) => renderContainer(fraction, `t-${index}`))}
              </div>
              <div className="divider"></div>
              <div className="bottom-row">
                {bottomRow.map((fraction, index) => renderContainer(fraction, `b-${index}`))}
              </div>
            </div>
          </div>
        </div>
      );
    }

    default:
      return (
        <div className="layout-visualization">
          <h3 className="layout-title">{layoutTitle['default']}</h3>
          <div className="layout-description">
            {renderLayoutInfoTable('linear', selectedFractions.length)}
          </div>
          <div className="layout-container">
            <div className="container-row">
              {selectedFractions.map(renderContainer)}
            </div>
          </div>
        </div>
      );
  }
};

export default LayoutVisualization;